import { Box, Flex, ActionIcon as MantineActionIcon, Text, Textarea, TextInput } from "@mantine/core";
import { IIssueApi } from "@schemas/issueSchema";
import { IconX } from "@tabler/icons-react";
import React from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import SimpleStampCheck from "../../../../../../components/SimpleStampCheck/SimpleStampCheck";
import { getProjectIdFromUrl } from "../../../../../utils";
import { useDoUpdateIssueMutation } from "../../../issueRTKLegacy";
import { selectIssueBoard } from "../../../issueSelectors";

type Props = {
  content: IIssueApi;
  onClose: () => void;
};

type SJADraft = {
  A: string;
  B: string;
  C: string;
  D: string;
};

type DraftKey = keyof SJADraft;

export const KanbanCardSJADetail: React.FC<Props> = ({ content, onClose }) => {
  const issueBoard = useSelector(selectIssueBoard);
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);

  const [updateIssue, { isLoading: isUpdating }] = useDoUpdateIssueMutation();

  const getSJAString = (value: unknown): string => (typeof value === "string" ? value : "");

  const [draft, setDraft] = React.useState<SJADraft>({
    A: getSJAString(content.otherAttributesData?.A),
    B: getSJAString(content.otherAttributesData?.B),
    C: getSJAString(content.otherAttributesData?.C),
    D: getSJAString(content.otherAttributesData?.D),
  });

  React.useEffect(() => {
    setDraft({
      A: getSJAString(content.otherAttributesData?.A),
      B: getSJAString(content.otherAttributesData?.B),
      C: getSJAString(content.otherAttributesData?.C),
      D: getSJAString(content.otherAttributesData?.D),
    });
  }, [content._id, content.otherAttributesData]);

  const saveField = (key: DraftKey, value: string) => {
    const oldValue = content.otherAttributesData?.[key];

    if (value === oldValue) return;

    updateIssue({
      projectId,
      issueBoardId: issueBoard?._id,
      issueId: content._id,
      attr: `otherAttributesData.${key}`,
      value,
      oldValue,
    });
  };

  return (
    <Box p="md" h="100%" bg="white">
      {/* Header */}
      <Flex align="center" mb="sm">
        <Text fw={700} size="md">
          {content.connectedIssue?.title}
        </Text>
        <MantineActionIcon onClick={onClose} variant="subtle">
          <IconX />
        </MantineActionIcon>
      </Flex>
      {/* Content */}
      <Flex direction="column" gap="sm">
        <TextInput
          size="sm"
          label="Risiko"
          value={draft.B}
          onChange={(e) => {
            const value = e.target.value;
            setDraft((d) => ({ ...d, B: value }));
          }}
          onBlur={() => saveField("B", draft.B)}
        />
        <Textarea
          size="sm"
          label="Tiltak"
          value={draft.A}
          autosize
          minRows={2}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setDraft((d) => ({ ...d, A: value }));
          }}
          onBlur={() => saveField("A", draft.A)}
        />
        <Textarea
          size="sm"
          label="Restrisiko"
          value={draft.C}
          autosize
          minRows={2}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setDraft((d) => ({ ...d, C: value }));
          }}
          onBlur={() => saveField("C", draft.C)}
        />
        <Textarea
          size="sm"
          label="Kommentar"
          value={draft.D}
          autosize
          minRows={2}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setDraft((d) => ({ ...d, D: value }));
          }}
          onBlur={() => saveField("D", draft.D)}
        />
        <SimpleStampCheck
          toggled={Boolean(content.otherAttributesData?.E)}
          toggle={(newValue) => {
            updateIssue({
              projectId: projectId,
              issueBoardId: issueBoard?._id,
              issueId: content._id,
              attr: "otherAttributesData.E",
              value: newValue,
              oldValue: content.otherAttributesData?.E,
            });
          }}
        />
      </Flex>
    </Box>
  );
};
